import React, { useState, useMemo, useEffect } from 'react';
import Sidebar from './sidebar/Sidebar';
import './ChooseTemplate.css';
import downloadIcon from '../assets/download.svg';
import AddDocumentModal from './modals/CreateBlankModal';
import { FaSearch, FaCaretDown } from "react-icons/fa";

const ChooseTemplate = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const savedColumns = localStorage.getItem('chooseTemplateVisibleColumns');
    return savedColumns
      ? JSON.parse(savedColumns)
      : {
          checkbox: true,
          link: true,
          name: true,
          type: true,
          createdBy: true,
          date: true,
          actions: true,
        };
  });

  useEffect(() => {
    localStorage.setItem('chooseTemplateVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Simulate data fetching
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000); // Simulate 1-second delay
    return () => clearTimeout(timer);
  }, []);

  // Templates data
  const templates = [
    {
      id: 1,
      name: 'APACMENA - Confidentiality MNDA',
      type: 'MNDA',
      createdBy: 'Admin',
      date: '22-08-2025',
      link: 'https://criticalrivertechnologies.sharepoint.com/:w:/s/CR-CLM/EfrUeoAjB6tGonMO36fzgJQBk3C3WvK9tJPJkt5gQeIttA?e=srpX4I',
    },
    {
      id: 2,
      name: 'APACMENA - APACMENA - MSA Client',
      type: 'MSA Client',
      createdBy: 'Admin',
      date: '22-08-2025',
      link: 'https://criticalrivertechnologies.sharepoint.com/:w:/s/CR-CLM/EQmybYwCO4JOr7_GZF9uMSoBFGBU9uyCGjVMR01dmPflhA?e=Big5wJ',
    },
    {
      id: 3,
      name: 'APACMENA - MSA Vendor',
      type: 'MSA Vendor',
      createdBy: 'Admin',
      date: '22-08-2025',
      link: 'https://criticalrivertechnologies.sharepoint.com/:w:/s/CR-CLM/EUYBpXD0qqxMlWBLw4ZLRDcBgRMBFBpdiA_pNqpvW3ifIw?e=sj7GRJ',
    },
  ];

  // Filtering logic
  const filteredTemplates = templates.filter((t) => {
    const matchesSearch =
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.createdBy.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = contractTypeFilter ? t.type === contractTypeFilter : true;

    return matchesSearch && matchesType;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, filteredTemplates.length);
  const currentTemplates = useMemo(
    () => filteredTemplates.slice(startIndex, endIndex),
    [filteredTemplates, startIndex, endIndex]
  );

  // Handle View Template (Open link in new tab)
const handleViewTemplate = (template) => {
  if (template.link) {
    setPreviewUrl(template.link);
  }
};


  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setIsAddDocumentModalOpen(true);
  };

  const handleTemplateSelect = (templateId) => {
    if (selectedTemplates.includes(templateId)) {
      setSelectedTemplates(selectedTemplates.filter((id) => id !== templateId));
    } else {
      setSelectedTemplates([...selectedTemplates, templateId]);
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem("chooseTemplateVisibleColumns", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div className="choose-template-container">
      <Sidebar />
      <div className="main-content">
        {loading ? (
          <div className="loading-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
          }}>
            <div className="spinner" style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f4f6',
              borderTop: '5px solid #ff6b35',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="header-section2">
              <h1 style={{ color: '#ff6b35', fontSize: '27px' }} className="title">Choose Template</h1><br/>
              <div className="right-controls" style={{ display: "flex", gap: "16px", alignItems: "center", width: "100%" }}>
                <div className="search-input-wrapper" style={{ width: "100%" }}>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search documents"
                    style={{ border: "1px solid #000", borderRadius: "6px", width: "100%" }}
                  />
                </div>
                <div className="dropdown-wrapper" style={{ width: "100%" }}>
                  <select
                    className="dropdown"
                    value={contractTypeFilter}
                    onChange={(e) => setContractTypeFilter(e.target.value)}
                    style={{ width: "100%", border: "1px solid #000", borderRadius: "6px" }}
                  >
                    <option value="">Contract Type</option>
                    <option value="MNDA">MNDA</option>
                    <option value="MSA Client">MSA Client</option>
                    <option value="MSA Vendor">MSA Vendor</option>
                  </select>
                  <FaCaretDown className="dropdown-icon" />
                </div>
              </div>
            </div>

            {/* Results */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
              <button
                onClick={() => setShowColumnOptions(true)}
                style={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  background: 'transparent',
                  color: '#4A4A4A',
                  border: '1px solid #000',
                  fontSize: '16px',
                  fontWeight: 500,
                }}
              >
                ⚙ Column options
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4A4A4A', fontSize: '16px', fontWeight: 500 }}>
                <div className="results-summary">
                  Results: {startIndex + 1}-{endIndex} of {filteredTemplates.length}
                </div>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    paddingLeft: '10px',
                    color: '#4A4A4A',
                    fontSize: '16px',
                    fontWeight: 500,
                    backgroundColor: '#e5e7eb',
                    width: '60px',
                    height: '30px',
                    borderRadius: '6px',
                    border: '1px solid #000',
                  }}
                >
                  {[5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="table-container">
              <table className="template-table">
                <thead>
                  <tr>
                    {visibleColumns.checkbox && <th></th>}
                    {visibleColumns.name && <th>Template Name</th>}
                    {visibleColumns.type && <th>Contract Type</th>}
                    {visibleColumns.createdBy && <th>Created By</th>}
                    {visibleColumns.date && <th>Created Date</th>}
                    {visibleColumns.actions && <th className="actions-header">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {currentTemplates.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#4A5568' }}>
                        No data found
                      </td>
                    </tr>
                  ) : (
                    currentTemplates.map((template) => (
                      <tr key={template.id}>
                        {visibleColumns.checkbox && (
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedTemplates.includes(template.id)}
                              onChange={() => handleTemplateSelect(template.id)}
                              style={{ marginLeft: '25px' }}
                            />
                          </td>
                        )}
                        {visibleColumns.name && (
                          <td style={{ overflow: 'hidden' }}>
                            {template.name}
                          </td>
                        )}
                        {visibleColumns.type && <td>{template.type}</td>}
                        {visibleColumns.createdBy && <td>{template.createdBy}</td>}
                        {visibleColumns.date && <td>{template.date}</td>}
                        {visibleColumns.actions && (
                          <td className="actions-cell">
                            <button className="action-btn download-btn" title="Download">
                              <img
                                src={downloadIcon}
                                alt="Download"
                                className="action-image1"
                                style={{
                                  filter: 'invert(45%) sepia(94%) saturate(2478%) hue-rotate(346deg) brightness(96%) contrast(97%)',
                                }}
                              />
                            </button>
                            <button className="action-btn view-btn" title="View" onClick={() => handleViewTemplate(template)}>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ff6b35"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                            <button title="Use" className="action-btn use-btn" onClick={() => handleUseTemplate(template)}>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ff6b35"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polygon points="5 3 19 12 5 21 5 3" />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <nav style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '18px',
                      border: '1px solid #ff6b35',
                      background: currentPage === 1 ? '#f3f4f6' : '#fff',
                      color: currentPage === 1 ? '#9ca3af' : '#ff6b35',
                    }}
                  >
                    {'<'}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '18px',
                        border: '1px solid #ff6b35',
                        background: page === currentPage ? '#ff6b35' : '#fff',
                        color: page === currentPage ? '#fff' : '#ff6b35',
                        cursor: 'pointer',
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '18px',
                      border: '1px solid #ff6b35',
                      background: currentPage === totalPages ? '#f3f4f6' : '#fff',
                      color: currentPage === totalPages ? '#9ca3af' : '#ff6b35',
                    }}
                  >
                    {'>'}
                  </button>
                </nav>
              </div>
            )}

            {/* Add Document Modal */}
            <AddDocumentModal
              isOpen={isAddDocumentModalOpen}
              onClose={() => {
                setIsAddDocumentModalOpen(false);
                setSelectedTemplate(null);
              }}
              onSelectTemplate={() => {}}
              selectedTemplate={selectedTemplate}
            />

            {/* Column Options Modal */}
            {showColumnOptions && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(6px)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  zIndex: 1000,
                }}
              >
                <div
                  style={{
                    width: '340px',
                    background: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginTop: '100px',
                    marginRight: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 12px',
                      fontWeight: '700',
                      background: '#ff6b35',
                      color: '#fff'
                    }}
                  >
                    <div>Column Options</div>
                    <button
                      onClick={() => setShowColumnOptions(false)}
                      aria-label="Close"
                      style={{ background: 'transparent', border: 'none', fontSize: '25px', cursor: 'pointer', color: '#fff' }}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: '-8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      padding: '0 12px 8px',
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                  </div>
                  <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '10px 12px' }}>
                    {['checkbox', 'link', 'name', 'type', 'createdBy', 'date', 'actions'].map(
                      (key) => (
                        <div
                          key={key}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #f3f4f6',
                          }}
                        >
                          <span style={{ fontSize: '14px', color: '#111827' }}>
                            {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                          </span>
                          <button
                            onClick={() => toggleColumn(key)}
                            aria-label={visibleColumns[key] ? `Hide ${key}` : `Show ${key}`}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#000000"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                              {!visibleColumns[key] && (
                                <line x1="2" y1="2" x2="18" y2="18" stroke="#000000" strokeWidth="3" />
                              )}
                            </svg>
                          </button>
                        </div>
                      )
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px 12px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => setShowColumnOptions(false)}
                      style={{ background: 'transparent', color: '#ff6b35', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowColumnOptions(false)}
                      style={{ background: '#ff6b35', color: '#fff', border: '1px solid #ff6b35', padding: '8px 14px', borderRadius: '6px' }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChooseTemplate;